import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import '@/styles/termsModal.less';
import Button from '../Forms/Button/index.js';
import Checkbox from '../Forms/Checkbox/index.js';
import TermsViewer from '../../features/TermsViewer/index.js';
import { termsService } from '@/services/auth/termsService.js';
import { userService } from '@/services/auth/userService.js';

const TermsModal = ({ onAccept, onSkip }) => {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [terms, setTerms] = useState([]);
  const [currentTermIndex, setCurrentTermIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const auth = userService.isAuthenticated();
      setIsAuthenticated(auth);

      if (!auth) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const activeTerms = await termsService.getActiveTerms();
        if (activeTerms && activeTerms.length > 0) {
          setTerms(activeTerms);
          activeTerms.forEach((term) => termsService.preloadTermContent(term.url));
        } else {
          onAccept();
        }
      } catch (err) {
        console.error('Error fetching terms:', err);
        toast.error(t('terms.errors.loadFailed', { defaultValue: 'Não foi possível carregar os termos. Tente novamente mais tarde.' }));
        onSkip();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [t, onAccept, onSkip]);

  const handleAccept = async () => {
    try {
      const currentTerm = terms[currentTermIndex];
      if (!currentTerm) return;

      await termsService.acceptTerm(currentTerm.id, currentTerm);
      setAccepted(false);

      if (currentTermIndex < terms.length - 1) {
        setCurrentTermIndex(currentTermIndex + 1);
      } else {
        onAccept();
      }
    } catch (err) {
      console.error('Error accepting terms:', err);
      const errorMessage = err.code === 'INVALID_ASSET_TYPE'
        ? t('terms.errors.invalidAssetType', { defaultValue: 'Tipo de termo inválido. Por favor, tente novamente.' })
        : t('terms.errors.acceptFailed', { defaultValue: 'Erro ao aceitar os termos. Tente novamente mais tarde.' });
      toast.error(errorMessage);
      onSkip();
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-4">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (terms.length === 0) return null;

  const currentTerm = terms[currentTermIndex];
  const isLastTerm = currentTermIndex === terms.length - 1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white w-[calc(100%-60px)] h-[calc(100vh-60px)] overflow-hidden flex flex-col rounded-lg p-2">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">{currentTerm.title || t('terms.title')}</h2>
          <span className="text-sm text-gray-500">
            {t('terms.progress', { current: currentTermIndex + 1, total: terms.length })}
          </span>
        </div>

        <div className="flex-1 overflow-auto">
          <TermsViewer fileUrl={currentTerm.url} />
        </div>

        <div className="p-4 border-t">
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              checked={accepted}
              onChange={() => setAccepted(!accepted)}
              label={t('terms.acceptance')}
              className="w-full text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
          </div>
          <Button
            onClick={handleAccept}
            disabled={!accepted}
            className="w-full mb-4"
          >
            {isLastTerm ? t('terms.buttons.finishButton') : t('terms.buttons.nextButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

TermsModal.propTypes = {
  onAccept: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default TermsModal;
